import React, { memo, useCallback, useMemo } from 'react'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import * as Analytics from 'analytics/analytics'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'
import { useInView } from 'react-intersection-observer'

import Heading from 'LuxKit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { skip, take, without } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import type { OfferListEventsDispatchAction } from 'components/OfferList/OfferListEventsContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import type { OfferListAndAction } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import getOfferListKey from 'lib/offer/offerListKey'
import Sheet from 'components/utils/Sheet'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  title?: string
  offerId?: string
  filters?: App.OfferListFilters
  trackingDescription: string
  propertyId?: string
  skipFirstNOffers?: number
  useList?: boolean
  overrideOfferList?: App.OfferList
  maxOffers?: number
  canJoinLereGraphAbTest?: boolean
  // use for overriding the prices and values when having checkIn/checkOut/Occupancy
  enableDateCheckingForPriceDisplay?: boolean
}

const MINIMUM_OFFERS_TO_DISPLAY = 3
const DEFAULT_MAX_OFFERS = 8

const RECOMMENDATION_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  'bedbank_hotel',
]

function OfferRecommendations(props: Props) {
  const {
    title = 'You may also like',
    offerId,
    filters = emptyFilters,
    trackingDescription,
    skipFirstNOffers,
    useList = false,
    overrideOfferList,
    maxOffers = DEFAULT_MAX_OFFERS,
    enableDateCheckingForPriceDisplay = false,
  } = props

  const handleListEvent = useCallback((dispatchAction: OfferListEventsDispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick && (filters.lere === 'graph' || filters.lere === 'twoTowers')) {
      Analytics.trackClientEvent({
        subject: 'similar-offer-click',
        action: `ab-value: ${dispatchAction.position + 1}`,
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '5827605312569344',
        optimizelyEventKey: 'similar_carousel_product_click',
      })
    }
  }, [filters])

  const twoTowersFilters = useMemo((): App.OfferListFilters | undefined => {
    if (filters && offerId) {
      return {
        ...filters,
        lere: 'twoTowers',
        baseOfferId: offerId,
        rankerEnabled: false,
        useClusters: true,
      }
    }
    return undefined
  }, [filters, offerId])

  const useOverrideList = !!overrideOfferList?.fetching || !!overrideOfferList?.offerIds?.length
  const defaultOfferList = useOfferList({ ...filters, offerTypes: RECOMMENDATION_TYPES }, { disabled: useOverrideList })
  const twoTowersOfferList = useOfferList(twoTowersFilters, { disabled: useOverrideList || !twoTowersFilters || filters.lere !== 'graph' })
  const twoTowersAbTestVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereRankerClustering))

  // Monitor when section first enters viewport (trigger for AB test)
  const [sectionRef, sectionInView] = useInView({ triggerOnce: true, threshold: 0.5 })

  const canTriggerExperiment = defaultOfferList.offerIds.length >= MINIMUM_OFFERS_TO_DISPLAY && twoTowersOfferList.offerIds.length >= MINIMUM_OFFERS_TO_DISPLAY && !useOverrideList && config.BRAND === 'luxuryescapes'
  const isTwoTowersExperimentGroup = twoTowersAbTestVariation === 'on' && canTriggerExperiment

  useOptimizelyExperiment(OptimizelyExperiments.lereRankerClustering, canTriggerExperiment && sectionInView)

  const offerList: App.OfferList = useMemo(() => {
    if (useOverrideList) {
      return overrideOfferList
    }
    if (isTwoTowersExperimentGroup) {
      return twoTowersOfferList
    }
    return defaultOfferList
  }, [defaultOfferList, overrideOfferList, useOverrideList, twoTowersOfferList, isTwoTowersExperimentGroup])

  const selectedDatesAndRoomsFilter: App.OfferListFilters | undefined = useMemo(() => {
    return enableDateCheckingForPriceDisplay ? {
      checkIn: filters.checkIn,
      checkOut: filters.checkOut,
      rooms: filters.rooms,
    } : undefined
  }, [enableDateCheckingForPriceDisplay, filters])

  const offerIds = useMemo(() => {
    // offer list ids without source offer
    let offerCandidates = without(offerList.offerIds, offerId ?? '')
    // skip first few offers if specified
    if (skipFirstNOffers) {
      offerCandidates = skip(offerCandidates, skipFirstNOffers)
    }
    // take max offers
    offerCandidates = take(offerCandidates, maxOffers)
    return offerCandidates
  }, [offerId, offerList.offerIds, skipFirstNOffers, maxOffers])

  const algorithm = isTwoTowersExperimentGroup ? 'TwoTowers' : (filters.lere === 'graph' ? 'Graph' : (filters.propertyId ? 'Search Nearby' : 'Algolia'))
  const tracking = useOfferListTracking(
    `Recommendations-${algorithm}-${trackingDescription}`, {
      ga: {
        category: 'Recommendations',
        label: `${algorithm}-${trackingDescription}`,
      },
      key: getOfferListKey(filters),
    },
  )

  const isIdsFetching = offerList.fetching

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  const toShowOfferList = useMemo<App.OfferList>(() => ({
    fetching: isIdsFetching,
    offerIds,
    key: 'offerRecommendations',
  }), [isIdsFetching, offerIds])

  const overrideOfferListAndAction = useMemo<OfferListAndAction>(() => ({
    action: OfferListAction.OVERRIDE,
    offerList: toShowOfferList,
  }), [toShowOfferList])
  const insufficientReco = offerIds.length < MINIMUM_OFFERS_TO_DISPLAY && !isIdsFetching
  if (insufficientReco) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={handleListEvent}>
      <Sheet background="neutral-eight" padding="40 0" ref={sectionRef}>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">{title}</Heading>
          </LayoutContainer>
          {!useList && <OfferListCarousel
            overrideOfferList={toShowOfferList}
            additionalOfferUrlQuery={additionalQueryParams}
            selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
          />}
          {useList && <OfferList
            extraOfferListAndAction={overrideOfferListAndAction}
            tileStyle="search"
          />}
        </VerticalSpacer>
      </Sheet>
    </OfferListEventsProvider>
  )
}

export default memo(OfferRecommendations)
